import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllEnrollments,
  getEnrollmentById,
  createEnrollment,
  updateEnrollment,
  deleteEnrollment
} from '../services/enrollmentService';
import Notification from '../components/Common/Notification';

export const EnrollmentContext = createContext();

export const EnrollmentProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    if (!user && !storedUser) {
      console.error('User or token not available');
      return {};
    }

    const token = user?.token || JSON.parse(storedUser).token;
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllEnrollments = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllEnrollments(authHeaders);
      setEnrollments(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching enrollments.';
      showNotification(errorMessage, true);
    }
  };

  const fetchEnrollmentById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getEnrollmentById(id, authHeaders);
      setSelectedEnrollment(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching enrollment with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addEnrollment = async (enrollmentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newEnrollment = await createEnrollment(enrollmentData, authHeaders);
      setEnrollments(prevEnrollments => [...prevEnrollments, newEnrollment]);
      showNotification('Enrollment added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding enrollment.';
      showNotification(errorMessage, true);
    }
  };

  const editEnrollment = async (id, enrollmentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedEnrollment = await updateEnrollment(id, enrollmentData, authHeaders);
      setEnrollments(prevEnrollments =>
        prevEnrollments.map(enrollment => (enrollment.id === id ? updatedEnrollment : enrollment))
      );
      showNotification('Enrollment updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating enrollment.';
      showNotification(errorMessage, true);
    }
  };

  const removeEnrollment = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteEnrollment(id, authHeaders);
      setEnrollments(prevEnrollments => prevEnrollments.filter(enrollment => enrollment.id !== id));
      showNotification('Enrollment removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing enrollment.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllEnrollments();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <EnrollmentContext.Provider
      value={{
        enrollments,
        selectedEnrollment,
        fetchAllEnrollments,
        fetchEnrollmentById,
        addEnrollment,
        editEnrollment,
        removeEnrollment,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </EnrollmentContext.Provider>
  );
};
